import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import Tooltip from 'components/Tooltip';
import { categoriesService, metalsService } from '../../../services/metalguard';


// Default metal names (cannot be deleted)
const DEFAULT_METAL_NAMES = [
  'acero inox', 'inox', 'aluminio', 'bronce', 'cobre',
  'hierro', 'acero', 'laton', 'niquel', 'plomo', 'zinc',
];

const isDefaultMetal = (metal) =>
  metal?.is_default === true;

const METAL_COLORS = [
  '#64748B', '#3B82F6', '#92400E', '#EA580C', '#475569',
  '#D97706', '#7C3AED', '#6B7280', '#059669', '#0EA5E9',
  '#F43F5E', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899',
];

const getMetalColor = (metal, index) => {
  if (metal?.name?.toLowerCase()?.includes('acero inox') || metal?.name?.toLowerCase()?.includes('inox')) return '#64748B';
  if (metal?.name?.toLowerCase()?.includes('aluminio')) return '#3B82F6';
  if (metal?.name?.toLowerCase()?.includes('bronce')) return '#92400E';
  if (metal?.name?.toLowerCase()?.includes('cobre')) return '#EA580C';
  if (metal?.name?.toLowerCase()?.includes('hierro') || metal?.name?.toLowerCase()?.includes('acero')) return '#475569';
  if (metal?.name?.toLowerCase()?.includes('laton')) return '#D97706';
  if (metal?.name?.toLowerCase()?.includes('niquel')) return '#7C3AED';
  if (metal?.name?.toLowerCase()?.includes('plomo')) return '#6B7280';
  if (metal?.name?.toLowerCase()?.includes('zinc')) return '#059669';
  return METAL_COLORS?.[index % METAL_COLORS?.length];
};

const DEFAULT_ROW = (metalId) => ({
  id: `new-${Date.now()}-${Math.random()}`,
  metalId,
  display_name: '',
  code: '',
  default_yield_pct: 85,
  default_discount_mode: 'PCT_OF_BENCHMARK',
  default_discount_value: 5,
  default_processing_cost_eur_per_ton: 50,
  default_logistics_cost_eur_per_ton: 30,
  default_processing_days: 7,
  contamination_risk_score: 0.1,
  notes: '',
  _isNew: true,
});

const EMPTY_METAL_FORM = {
  name: '',
  benchmark_type: 'LME',
  benchmark_symbol: '',
  benchmark_currency: 'USD',
  base_unit: 'ton',
};

const SLUG_RE = /^[a-z0-9-]+$/;

function validateCategoryRow(row, allRowsForMetal) {
  const errs = {};
  if (!row?.display_name?.trim()) errs.display_name = 'El nombre es obligatorio';
  else if (row?.display_name?.trim()?.length < 2) errs.display_name = 'El nombre es obligatorio (mín. 2 caracteres)';
  else if (row?.display_name?.trim()?.length > 100) errs.display_name = 'Máximo 100 caracteres';

  if (!row?.code?.trim()) errs.code = 'El código es obligatorio';
  else if (!SLUG_RE?.test(row?.code?.trim())) errs.code = 'Solo letras minúsculas, números y guiones';
  else {
    const duplicate = allRowsForMetal?.some(r => r?.id !== row?.id && r?.code?.trim() === row?.code?.trim());
    if (duplicate) errs.code = 'Este código ya existe';
  }

  const yld = parseFloat(row?.default_yield_pct);
  if (isNaN(yld) || yld < 0 || yld > 100) errs.default_yield_pct = 'Debe estar entre 0 y 100';

  const discVal = parseFloat(row?.default_discount_value);
  if (row?.default_discount_mode === 'PCT_OF_BENCHMARK') {
    if (isNaN(discVal) || discVal < -100 || discVal > 100) errs.default_discount_value = 'Descuento porcentual fuera de rango';
  }

  const procCost = parseFloat(row?.default_processing_cost_eur_per_ton);
  if (isNaN(procCost) || procCost < 0) errs.default_processing_cost_eur_per_ton = 'Debe ser un valor positivo';

  const logCost = parseFloat(row?.default_logistics_cost_eur_per_ton);
  if (isNaN(logCost) || logCost < 0) errs.default_logistics_cost_eur_per_ton = 'Debe ser un valor positivo';

  const days = parseInt(row?.default_processing_days);
  if (isNaN(days) || days < 0 || days > 365) errs.default_processing_days = 'Introduce un número de días válido';

  const risk = parseFloat(row?.contamination_risk_score);
  if (isNaN(risk) || risk < 0 || risk > 1) errs.contamination_risk_score = 'Debe estar entre 0 y 1';

  return errs;
}

function EditableCell({ value, onChange, onBlur, type = 'text', options, min, max, step, className = '', error }) {
  if (type === 'select') {
    return (
      <div>
        <select value={value} onChange={(e) => onChange(e?.target?.value)} onBlur={onBlur}
          className={`w-full bg-background border rounded px-2 py-1 text-xs text-foreground focus:outline-none focus:border-accent ${
            error ? 'border-red-500' : 'border-border'
          } ${className}`}
          style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem', borderColor: error ? 'var(--color-error)' : undefined }}>
          {options?.map((opt) => <option key={opt?.value} value={opt?.value}>{opt?.label}</option>)}
        </select>
        {error && <p className="text-xs mt-0.5" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.625rem' }}>{error}</p>}
      </div>
    );
  }
  return (
    <div>
      <input type={type} value={value} onChange={(e) => onChange(type === 'number' ? parseFloat(e?.target?.value) || 0 : e?.target?.value)}
        onBlur={onBlur}
        min={min} max={max} step={step}
        className={`w-full bg-background border rounded px-2 py-1 text-xs text-foreground focus:outline-none focus:border-accent ${
          error ? 'border-red-500' : 'border-border'
        } ${className}`}
        style={{ fontFamily: type === 'number' ? '"JetBrains Mono", monospace' : '"IBM Plex Sans", sans-serif', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', borderColor: error ? 'var(--color-error)' : undefined }}
      />
      {error && <p className="text-xs mt-0.5" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.625rem' }}>{error}</p>}
    </div>
  );
}

function MetalGroupModal({ open, onClose, onSave, initialValues, saving, existingMetals }) {
  const [form, setForm] = useState(EMPTY_METAL_FORM);
  const [modalErrors, setModalErrors] = useState({});
  const [modalTouched, setModalTouched] = useState({});

  useEffect(() => {
    if (open) {
      setForm(initialValues ? {
        name: initialValues?.name || '',
        benchmark_type: initialValues?.benchmark_type || 'LME',
        benchmark_symbol: initialValues?.benchmark_symbol || '',
        benchmark_currency: initialValues?.benchmark_currency || 'USD',
        base_unit: initialValues?.base_unit || 'ton',
      } : EMPTY_METAL_FORM);
      setModalErrors({});
      setModalTouched({});
    }
  }, [open, initialValues]);

  if (!open) return null;

  const isEdit = !!initialValues;

  const validateModalField = (name, value, allForm) => {
    if (name === 'name') {
      if (!value?.trim()) return 'El nombre del grupo es obligatorio';
      if (value?.trim()?.length < 2) return 'El nombre del grupo es obligatorio (mín. 2 caracteres)';
      const isDuplicate = existingMetals?.some(
        m => m?.name?.toLowerCase() === value?.trim()?.toLowerCase() && m?.id !== initialValues?.id
      );
      if (isDuplicate) return 'Este nombre ya existe';
    }
    if (name === 'benchmark_type') {
      if (!value) return 'Selecciona el tipo de benchmark';
    }
    if (name === 'benchmark_symbol') {
      const bType = allForm?.benchmark_type || form?.benchmark_type;
      if (bType === 'LME' && !value?.trim()) return 'El símbolo LME es obligatorio';
    }
    return null;
  };

  const handleModalBlur = (name) => {
    setModalTouched(prev => ({ ...prev, [name]: true }));
    const err = validateModalField(name, form?.[name], form);
    setModalErrors(prev => ({ ...prev, [name]: err }));
  };

  const handleModalChange = (name, value) => {
    setForm(f => ({ ...f, [name]: value }));
    if (modalTouched?.[name]) {
      const err = validateModalField(name, value, { ...form, [name]: value });
      setModalErrors(prev => ({ ...prev, [name]: err }));
    } else {
      setModalErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const fields = ['name', 'benchmark_type', 'benchmark_symbol'];
    const newErrors = {};
    fields?.forEach(f => {
      const err = validateModalField(f, form?.[f], form);
      if (err) newErrors[f] = err;
    });
    setModalErrors(newErrors);
    setModalTouched(Object.fromEntries(fields?.map(f => [f, true])));
    if (Object.keys(newErrors)?.length > 0) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-md mx-4 p-6" style={{ zIndex: 1 }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-foreground font-semibold" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.0625rem' }}>
            {isEdit ? 'Editar grupo de metal' : 'Nuevo grupo de metal'}
          </h3>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <Icon name="X" size={16} color="currentColor" strokeWidth={2} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Nombre *</label>
            <input
              type="text"
              value={form?.name}
              onChange={(e) => handleModalChange('name', e?.target?.value)}
              onBlur={() => handleModalBlur('name')}
              placeholder="Ej: Titanio"
              className="w-full bg-background border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif', borderColor: modalErrors?.name ? 'var(--color-error)' : undefined }}
            />
            {modalErrors?.name && <p className="text-xs mt-1" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{modalErrors?.name}</p>}
          </div>
          {/* Benchmark Type */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Tipo de benchmark *</label>
            <select
              value={form?.benchmark_type}
              onChange={(e) => handleModalChange('benchmark_type', e?.target?.value)}
              onBlur={() => handleModalBlur('benchmark_type')}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif', borderColor: modalErrors?.benchmark_type ? 'var(--color-error)' : undefined }}
            >
              <option value="">Seleccionar...</option>
              <option value="LME">LME</option>
              <option value="REGIONAL_INDEX">REGIONAL_INDEX</option>
              <option value="MANUAL">MANUAL</option>
            </select>
            {modalErrors?.benchmark_type && <p className="text-xs mt-1" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{modalErrors?.benchmark_type}</p>}
          </div>
          {/* Benchmark Symbol */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Símbolo benchmark {form?.benchmark_type === 'LME' ? <span style={{ color: 'var(--color-error)' }}>*</span> : <span className="normal-case font-normal">(opcional)</span>}</label>
            <input
              type="text"
              value={form?.benchmark_symbol}
              onChange={(e) => handleModalChange('benchmark_symbol', e?.target?.value)}
              onBlur={() => handleModalBlur('benchmark_symbol')}
              placeholder="Ej: LME_COPPER"
              className="w-full bg-background border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent"
              style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', borderColor: modalErrors?.benchmark_symbol ? 'var(--color-error)' : undefined }}
            />
            {modalErrors?.benchmark_symbol && <p className="text-xs mt-1" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{modalErrors?.benchmark_symbol}</p>}
          </div>
          {/* Currency + Base Unit row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Moneda</label>
              <select
                value={form?.benchmark_currency}
                onChange={(e) => setForm(f => ({ ...f, benchmark_currency: e?.target?.value }))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Unidad base</label>
              <select
                value={form?.base_unit}
                onChange={(e) => setForm(f => ({ ...f, base_unit: e?.target?.value }))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                <option value="ton">ton</option>
                <option value="kg">kg</option>
              </select>
            </div>
          </div>
          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={saving}
              className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-accent text-white hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              {saving && <Icon name="Loader2" size={14} color="currentColor" strokeWidth={2} className="animate-spin" />}
              {isEdit ? 'Guardar cambios' : 'Crear grupo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteMetalModal({ open, onClose, onConfirm, metalName, saving }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6" style={{ zIndex: 1 }}>
        <div className="flex items-start gap-4 mb-5">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-error/15 flex items-center justify-center">
            <Icon name="AlertTriangle" size={20} color="var(--color-error)" strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-foreground font-semibold mb-1" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Eliminar grupo de metal</h3>
            <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', lineHeight: '1.5' }}>
              ¿Eliminar <strong className="text-foreground">{metalName}</strong>? Todas las categorías dentro de este grupo también serán eliminadas. Esta acción no se puede deshacer.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-error text-white hover:bg-error/90 disabled:opacity-50 transition-colors"
            style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            {saving && <Icon name="Loader2" size={14} color="currentColor" strokeWidth={2} className="animate-spin" />}
            Eliminar grupo
          </button>
        </div>
      </div>
    </div>
  );
}

function MetalGroupSection({ metal, color, categories, onUpdate, onAdd, onDelete, onEditMetal, onDeleteMetal, isDefault }) {
  const [expanded, setExpanded] = useState(true);
  const [rowErrors, setRowErrors] = useState({});
  const riskColor = (score) => score <= 0.1 ? 'text-success' : score <= 0.3 ? 'text-warning' : 'text-error';

  const handleCellChange = (rowId, field, value) => {
    onUpdate(metal?.id, rowId, field, value);
    // Clear error for this field on change
    setRowErrors(prev => ({
      ...prev,
      [rowId]: { ...(prev?.[rowId] || {}), [field]: null },
    }));
  };

  const handleCellBlur = (row, field) => {
    const allRows = categories;
    const updatedRow = { ...row };
    const errs = validateCategoryRow(updatedRow, allRows);
    setRowErrors(prev => ({
      ...prev,
      [row?.id]: { ...(prev?.[row?.id] || {}), [field]: errs?.[field] || null },
    }));
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden mb-4" data-tour="settings-metal-group">
      <div className="w-full flex items-center justify-between px-4 py-3 bg-muted">
        <button onClick={() => setExpanded((v) => !v)} className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity">
          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
          <span className="font-semibold text-foreground truncate" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9375rem' }}>{metal?.name}</span>
          <span className="px-2 py-0.5 rounded text-xs border border-border text-muted-foreground flex-shrink-0" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{metal?.benchmark_type}</span>
          {isDefault && (
            <span className="px-2 py-0.5 rounded text-xs bg-accent/15 text-accent border border-accent/30 flex-shrink-0" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontWeight: '600' }}>default</span>
          )}
          <span className="text-muted-foreground flex-shrink-0" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem' }}>{categories?.length || 0} categorias</span>
        </button>
        <div className="flex items-center gap-1 ml-3 flex-shrink-0">
          <button
            onClick={() => onEditMetal(metal)}
            className="flex items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
            title="Editar grupo"
          >
            <Icon name="Pencil" size={13} color="currentColor" strokeWidth={2} />
          </button>
          {!isDefault && (
            <button
              onClick={() => onDeleteMetal(metal)}
              className="flex items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-error hover:bg-error/10 transition-colors"
              title="Eliminar grupo"
            >
              <Icon name="Trash2" size={13} color="currentColor" strokeWidth={2} />
            </button>
          )}
          <button onClick={() => setExpanded((v) => !v)} className="flex items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-foreground transition-colors">
            <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={16} color="currentColor" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full" style={{ minWidth: '1100px' }}>
            <thead>
              <tr className="border-b border-border bg-card">
                {[
                  {
                    label: 'Nombre',
                    w: '140px',
                    tip: { title: 'Nombre (display_name)', description: 'Nombre visible de la categoría tal como aparece en toda la app.' },
                  },
                  {
                    label: 'Codigo',
                    w: '90px',
                    tip: { title: 'Código (slug)', description: 'Identificador interno estable. Solo minúsculas, números y guiones. No cambiar una vez creado.' },
                  },
                  {
                    label: 'Yield %',
                    w: '70px',
                    tip: { title: 'Yield por defecto', description: 'Yield por defecto para esta categoría. Se pre-rellena en la calculadora al seleccionarla.' },
                  },
                  {
                    label: 'Modo Descuento',
                    w: '140px',
                    tip: { title: 'Modo de descuento', description: 'ABS: el descuento se expresa en €/t fijos sobre el benchmark. PCT: el descuento es un porcentaje del benchmark.' },
                  },
                  {
                    label: 'Valor Desc.',
                    w: '80px',
                    tip: { title: 'Valor del descuento', description: 'Valor del descuento aplicado al precio benchmark para calcular el precio de venta esperado. Negativo = descuento, positivo = prima.' },
                  },
                  {
                    label: 'Coste Proc. €/t',
                    w: '100px',
                    tip: { title: 'Coste de procesamiento', description: 'Coste medio de procesamiento por tonelada para esta categoría. Se usa como valor por defecto en la calculadora.' },
                  },
                  {
                    label: 'Coste Log. €/t',
                    w: '100px',
                    tip: { title: 'Coste de logística', description: 'Coste medio de logística por tonelada. Incluye transporte, carga y descarga.' },
                  },
                  {
                    label: 'Dias Proc.',
                    w: '80px',
                    tip: { title: 'Días de procesamiento', description: 'Días medios desde compra hasta venta para esta categoría. Determina la ventana temporal del buffer de riesgo.' },
                  },
                  {
                    label: 'Riesgo Cont.',
                    w: '90px',
                    tip: { title: 'Riesgo de contaminación', description: 'Puntuación de riesgo de contaminación de 0 a 1. 0 = material limpio. 1 = alta probabilidad de impurezas que reducen el yield real.' },
                  },
                  {
                    label: 'Notas',
                    w: '160px',
                    tip: { title: 'Notas internas', description: 'Notas internas sobre la categoría: características del material, proveedores habituales, advertencias de calidad.' },
                  },
                  { label: '', w: '40px', tip: null },
                ]?.map((col) => (
                  <th key={col?.label} className="px-3 py-2 text-left text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.6875rem', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase', width: col?.w, minWidth: col?.w }}>
                    {col?.tip ? (
                      <Tooltip title={col?.tip?.title} description={col?.tip?.description}>
                        {col?.label}
                      </Tooltip>
                    ) : col?.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories?.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-6 text-center text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>Sin categorias. Anade una nueva fila.</td></tr>
              )}
              {categories?.map((row) => {
                const rErrs = rowErrors?.[row?.id] || {};
                return (
                  <tr key={row?.id} className="border-b border-border hover:bg-muted/40 transition-colors">
                    <td className="px-2 py-1.5"><EditableCell value={row?.display_name} onChange={(v) => handleCellChange(row?.id, 'display_name', v)} onBlur={() => handleCellBlur(row, 'display_name')} error={rErrs?.display_name} /></td>
                    <td className="px-2 py-1.5"><EditableCell value={row?.code} onChange={(v) => handleCellChange(row?.id, 'code', v)} onBlur={() => handleCellBlur(row, 'code')} className="uppercase" error={rErrs?.code} /></td>
                    <td className="px-2 py-1.5"><EditableCell value={row?.default_yield_pct} onChange={(v) => handleCellChange(row?.id, 'default_yield_pct', v)} onBlur={() => handleCellBlur(row, 'default_yield_pct')} type="number" min={0} max={100} step={0.5} error={rErrs?.default_yield_pct} /></td>
                    <td className="px-2 py-1.5"><EditableCell value={row?.default_discount_mode} onChange={(v) => handleCellChange(row?.id, 'default_discount_mode', v)} type="select" options={[{ value: 'PCT_OF_BENCHMARK', label: '% Benchmark' }, { value: 'ABS_EUR_PER_TON', label: '€/t Absoluto' }]} /></td>
                    <td className="px-2 py-1.5"><EditableCell value={row?.default_discount_value} onChange={(v) => handleCellChange(row?.id, 'default_discount_value', v)} onBlur={() => handleCellBlur(row, 'default_discount_value')} type="number" min={row?.default_discount_mode === 'ABS_EUR_PER_TON' ? undefined : -100} max={row?.default_discount_mode === 'PCT_OF_BENCHMARK' ? 100 : undefined} step={0.1} error={rErrs?.default_discount_value} /></td>
                    <td className="px-2 py-1.5"><EditableCell value={row?.default_processing_cost_eur_per_ton} onChange={(v) => handleCellChange(row?.id, 'default_processing_cost_eur_per_ton', v)} onBlur={() => handleCellBlur(row, 'default_processing_cost_eur_per_ton')} type="number" min={0} step={1} error={rErrs?.default_processing_cost_eur_per_ton} /></td>
                    <td className="px-2 py-1.5"><EditableCell value={row?.default_logistics_cost_eur_per_ton} onChange={(v) => handleCellChange(row?.id, 'default_logistics_cost_eur_per_ton', v)} onBlur={() => handleCellBlur(row, 'default_logistics_cost_eur_per_ton')} type="number" min={0} step={1} error={rErrs?.default_logistics_cost_eur_per_ton} /></td>
                    <td className="px-2 py-1.5"><EditableCell value={row?.default_processing_days} onChange={(v) => handleCellChange(row?.id, 'default_processing_days', v)} onBlur={() => handleCellBlur(row, 'default_processing_days')} type="number" min={0} max={365} step={1} error={rErrs?.default_processing_days} /></td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <EditableCell value={row?.contamination_risk_score} onChange={(v) => handleCellChange(row?.id, 'contamination_risk_score', v)} onBlur={() => handleCellBlur(row, 'contamination_risk_score')} type="number" min={0} max={1} step={0.01} error={rErrs?.contamination_risk_score} />
                        <span className={`text-xs font-bold ${riskColor(row?.contamination_risk_score)}`} style={{ fontFamily: '"JetBrains Mono", monospace' }}>{row?.contamination_risk_score <= 0.1 ? '●' : row?.contamination_risk_score <= 0.3 ? '◆' : '▲'}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5"><EditableCell value={row?.notes || ''} onChange={(v) => handleCellChange(row?.id, 'notes', v)} /></td>
                    <td className="px-2 py-1.5">
                      <button onClick={() => onDelete(metal?.id, row?.id)} className="flex items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-error hover:bg-error/10 transition-colors" title="Eliminar fila">
                        <Icon name="Trash2" size={13} color="currentColor" strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-2.5 border-t border-border bg-card">
            <button onClick={() => onAdd(metal?.id)} data-tour="settings-add-category" className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem', fontWeight: '500' }}>
              <Icon name="Plus" size={14} color="currentColor" strokeWidth={2.5} />
              Anadir categoria
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CategoryCatalogEditor({ onRegisterSave, onToast }) {
  const [categories, setCategories] = useState({});
  const [metals, setMetals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDeletes, setPendingDeletes] = useState([]);

  // Metal group modal state
  const [metalModal, setMetalModal] = useState({ open: false, metal: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, metal: null });
  const [metalSaving, setMetalSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [catsRes, metalsRes] = await Promise.all([
      categoriesService?.getAll(),
      metalsService?.getAll(),
    ]);
    const fetchedMetals = metalsRes?.data || [];
    setMetals(fetchedMetals);

    // Group categories by metal id
    const grouped = {};
    fetchedMetals?.forEach(m => { grouped[m.id] = []; });
    (catsRes?.data || [])?.forEach(cat => {
      const metalId = cat?.metal_id;
      if (metalId && grouped?.[metalId] !== undefined) {
        grouped[metalId] = [...(grouped?.[metalId] || []), {
          id: cat?.id,
          metalId: metalId,
          metal_id: cat?.metal_id,
          display_name: cat?.display_name,
          code: cat?.code,
          default_yield_pct: parseFloat(cat?.default_yield_pct),
          default_discount_mode: cat?.default_discount_mode,
          default_discount_value: parseFloat(cat?.default_discount_value),
          default_processing_cost_eur_per_ton: parseFloat(cat?.default_processing_cost_eur_per_ton),
          default_logistics_cost_eur_per_ton: parseFloat(cat?.default_logistics_cost_eur_per_ton),
          default_processing_days: cat?.default_processing_days,
          contamination_risk_score: parseFloat(cat?.contamination_risk_score),
          notes: cat?.notes || '',
        }];
      }
    });
    setCategories(grouped);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Register save function with parent
  useEffect(() => {
    if (!onRegisterSave) return;
    onRegisterSave(() => async () => {
      const allCats = Object.values(categories)?.flat();
      for (const cat of allCats) {
        if (cat?._isNew) {
          await categoriesService?.create({
            metal_id: cat?.metalId,
            code: cat?.code,
            display_name: cat?.display_name,
            default_yield_pct: cat?.default_yield_pct,
            default_discount_mode: cat?.default_discount_mode,
            default_discount_value: cat?.default_discount_value,
            default_processing_cost_eur_per_ton: cat?.default_processing_cost_eur_per_ton,
            default_logistics_cost_eur_per_ton: cat?.default_logistics_cost_eur_per_ton,
            default_processing_days: cat?.default_processing_days,
            contamination_risk_score: cat?.contamination_risk_score,
            notes: cat?.notes,
          });
        } else {
          await categoriesService?.update(cat?.id, {
            code: cat?.code,
            display_name: cat?.display_name,
            default_yield_pct: cat?.default_yield_pct,
            default_discount_mode: cat?.default_discount_mode,
            default_discount_value: cat?.default_discount_value,
            default_processing_cost_eur_per_ton: cat?.default_processing_cost_eur_per_ton,
            default_logistics_cost_eur_per_ton: cat?.default_logistics_cost_eur_per_ton,
            default_processing_days: cat?.default_processing_days,
            contamination_risk_score: cat?.contamination_risk_score,
            notes: cat?.notes,
          });
        }
      }
      for (const id of pendingDeletes) {
        if (!String(id)?.startsWith('new-')) {
          await categoriesService?.delete(id);
        }
      }
      setPendingDeletes([]);
    });
  }, [categories, metals, pendingDeletes, onRegisterSave]);

  const handleUpdate = (metalId, rowId, field, value) => {
    setCategories((prev) => ({
      ...prev,
      [metalId]: prev?.[metalId]?.map((row) => row?.id === rowId ? { ...row, [field]: value } : row),
    }));
  };

  const handleAdd = (metalId) => {
    setCategories((prev) => ({
      ...prev,
      [metalId]: [...(prev?.[metalId] || []), DEFAULT_ROW(metalId)],
    }));
  };

  const handleDelete = (metalId, rowId) => {
    setPendingDeletes(prev => [...prev, rowId]);
    setCategories((prev) => ({
      ...prev,
      [metalId]: prev?.[metalId]?.filter((row) => row?.id !== rowId),
    }));
  };

  // ── Metal group CRUD ──────────────────────────────────────────────────────

  const handleOpenCreateMetal = () => setMetalModal({ open: true, metal: null });
  const handleOpenEditMetal = (metal) => setMetalModal({ open: true, metal });
  const handleCloseMetalModal = () => { if (!metalSaving) setMetalModal({ open: false, metal: null }); };

  const handleSaveMetal = async (form) => {
    setMetalSaving(true);
    try {
      const payload = {
        name: form?.name?.trim(),
        benchmark_type: form?.benchmark_type,
        benchmark_symbol: form?.benchmark_symbol?.trim() || null,
        benchmark_currency: form?.benchmark_currency,
        base_unit: form?.base_unit,
      };
      if (metalModal?.metal) {
        // Edit
        const { error } = await metalsService?.update(metalModal?.metal?.id, payload);
        if (error) {
          onToast?.('Error al actualizar el grupo de metal', 'error');
        } else {
          onToast?.('Grupo de metal actualizado', 'success');
          setMetalModal({ open: false, metal: null });
          await loadData();
        }
      } else {
        // Create
        const { error } = await metalsService?.create(payload);
        if (error) {
          onToast?.('Error al crear el grupo de metal', 'error');
        } else {
          onToast?.('Grupo de metal creado', 'success');
          setMetalModal({ open: false, metal: null });
          await loadData();
        }
      }
    } finally {
      setMetalSaving(false);
    }
  };

  const handleOpenDeleteMetal = (metal) => setDeleteModal({ open: true, metal });
  const handleCloseDeleteModal = () => { if (!metalSaving) setDeleteModal({ open: false, metal: null }); };

  const handleConfirmDeleteMetal = async () => {
    if (!deleteModal?.metal) return;
    setMetalSaving(true);
    try {
      const { error } = await metalsService?.delete(deleteModal?.metal?.id);
      if (error) {
        onToast?.('Error al eliminar el grupo de metal', 'error');
      } else {
        onToast?.('Grupo de metal eliminado', 'success');
        setDeleteModal({ open: false, metal: null });
        await loadData();
      }
    } finally {
      setMetalSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-3">
          <Icon name="Loader2" size={22} color="var(--color-accent)" strokeWidth={2} className="animate-spin" />
          <span className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Cargando categorias...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Modals */}
      <MetalGroupModal
        open={metalModal?.open}
        onClose={handleCloseMetalModal}
        onSave={handleSaveMetal}
        initialValues={metalModal?.metal}
        saving={metalSaving}
        existingMetals={metals}
      />
      <DeleteMetalModal
        open={deleteModal?.open}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDeleteMetal}
        metalName={deleteModal?.metal?.name}
        saving={metalSaving}
      />

      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-foreground mb-1" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.125rem', fontWeight: '600' }}>Catalogo de Categorias</h3>
          <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>Gestiona las categorias de chatarra por grupo de metal. Edita directamente en la tabla.</p>
        </div>
        <button
          onClick={handleOpenCreateMetal}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-accent text-white hover:bg-accent/90 transition-colors flex-shrink-0"
          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          <Icon name="Plus" size={15} color="currentColor" strokeWidth={2.5} />
          Nuevo grupo de metal
        </button>
      </div>

      <div className="mb-4 flex items-center justify-between gap-4 p-3 bg-muted rounded-lg border border-border">
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-success" /><span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Riesgo bajo (≤0.10)</span></div>
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-warning" /><span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Riesgo medio (0.11-0.30)</span></div>
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-error" /><span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Riesgo alto (&gt;0.30)</span></div>
      </div>

      {metals?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Icon name="Layers" size={36} color="var(--color-muted-foreground)" strokeWidth={1.5} className="mb-3 opacity-50" />
          <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>No hay grupos de metal. Crea el primero.</p>
        </div>
      )}

      {metals?.map((metal, idx) => (
        <MetalGroupSection
          key={metal?.id}
          metal={metal}
          color={getMetalColor(metal, idx)}
          categories={categories?.[metal?.id] || []}
          onUpdate={handleUpdate}
          onAdd={handleAdd}
          onDelete={handleDelete}
          onEditMetal={handleOpenEditMetal}
          onDeleteMetal={handleOpenDeleteMetal}
          isDefault={isDefaultMetal(metal)}
        />
      ))}
    </div>
  );
}
